import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CompanyCapitalizationPolicy,
  CreateLvaItem,
  DisposeLvaItem,
  LvaCategory,
  LvaItem,
  LvaItemListRow,
  LvaLabelRequest,
  LvaMovementListRow,
  LvaStatus,
  MarkDamagedLvaItem,
  MarkLostLvaItem,
  ReassignCustodian,
  TransferLvaItem,
  UpdateLvaItem,
} from '@dcc-erp/shared';

import { api, API_BASE, apiBlob, apiJson, downloadBlob, HttpError, type ApiError } from '../api';
import { ENTRA_API_SCOPE, msalInstance } from '../auth/msal-config';

// Cache-key scope — keyed by companyId per the convention used in
// queries/ar.ts and queries/employees.ts.
const lvaKey = (companyId: string | undefined) => ['lva-items', companyId] as const;

export interface LvaItemFilters {
  categoryName?: string;
  branchId?: string;
  departmentId?: string;
  status?: LvaStatus;
}

export function useLvaItems(companyId: string | undefined, filters: LvaItemFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.categoryName) qs.set('categoryName', filters.categoryName);
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.departmentId) qs.set('departmentId', filters.departmentId);
  if (filters.status) qs.set('status', filters.status);
  const search = qs.toString();
  return useQuery({
    queryKey: [...lvaKey(companyId), filters],
    queryFn: () =>
      api<LvaItemListRow[]>(`/companies/${companyId}/lva-items${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useLvaItem(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['lva-items', companyId, id],
    queryFn: () => api<LvaItemListRow>(`/companies/${companyId}/lva-items/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Scan-to-detail lookup: resolve a single item by its human-readable code
// (LVA-YYYY-NNNNN) so the /lva/$code route can render the asset a QR points at.
// Enabled only when both companyId + code are present (code is company-scoped
// server-side; an unknown/other-company code → 404).
export function useLvaItemByCode(companyId: string | undefined, code: string | undefined) {
  return useQuery({
    queryKey: ['lva-item-by-code', companyId, code] as const,
    queryFn: () => api<LvaItemListRow>(`/companies/${companyId}/lva-items/by-code/${code}`),
    enabled: !!companyId && !!code,
  });
}

export function useCreateLvaItem(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: CreateLvaItem; idempotencyKey?: string }) =>
      api<LvaItem>(`/companies/${companyId}/lva-items`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lvaKey(companyId) });
      // The create posts a JV — invalidate journal-entries too so the GL view
      // reflects the expensing entry.
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// Plain field edit (PATCH). No movement row + no GL posting — edits are not a
// lifecycle event in Phase A, so we only refresh the lva-items list.
export function useUpdateLvaItem(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLvaItem }) =>
      apiJson<UpdateLvaItem, LvaItem>('PATCH', `/companies/${companyId}/lva-items/${id}`, data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: lvaKey(companyId) }),
  });
}

// Lifecycle mutations write an append-only LvaMovement on the server but post NO
// GL entry, so they only invalidate the lva-items list + the per-item movement
// stream — never journal-entries.
function invalidateLifecycle(qc: ReturnType<typeof useQueryClient>, companyId: string | undefined) {
  void qc.invalidateQueries({ queryKey: lvaKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['lva-movements', companyId] });
}

export function useTransferLvaItem(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TransferLvaItem }) =>
      apiJson<TransferLvaItem, LvaItem>(
        'POST',
        `/companies/${companyId}/lva-items/${id}/transfer`,
        data,
      ),
    onSuccess: () => invalidateLifecycle(qc, companyId),
  });
}

export function useReassignCustodian(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReassignCustodian }) =>
      apiJson<ReassignCustodian, LvaItem>(
        'POST',
        `/companies/${companyId}/lva-items/${id}/reassign-custodian`,
        data,
      ),
    onSuccess: () => invalidateLifecycle(qc, companyId),
  });
}

export function useMarkDamaged(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MarkDamagedLvaItem }) =>
      apiJson<MarkDamagedLvaItem, LvaItem>(
        'POST',
        `/companies/${companyId}/lva-items/${id}/mark-damaged`,
        data,
      ),
    onSuccess: () => invalidateLifecycle(qc, companyId),
  });
}

export function useMarkLost(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MarkLostLvaItem }) =>
      apiJson<MarkLostLvaItem, LvaItem>(
        'POST',
        `/companies/${companyId}/lva-items/${id}/mark-lost`,
        data,
      ),
    onSuccess: () => invalidateLifecycle(qc, companyId),
  });
}

// Upgraded body: dispose now requires reason + approvedBy (server-enforced SoD
// approvedBy != submittedBy). Still posts NO JV — the asset was expensed at
// acquisition; this is a status flip + a DISPOSE movement.
export function useDisposeLvaItem(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DisposeLvaItem }) =>
      apiJson<DisposeLvaItem, LvaItem>(
        'POST',
        `/companies/${companyId}/lva-items/${id}/dispose`,
        data,
      ),
    onSuccess: () => invalidateLifecycle(qc, companyId),
  });
}

// Authenticated label-sheet download. The labels endpoint is POST (the request
// body carries the item selection + layout), but lib/api.ts `downloadFile` is
// GET-only, so we POST through `apiBlob` and hand the returned PDF blob to
// `downloadBlob` for the browser save. Filename mirrors the server's
// Content-Disposition (lva-labels-<ymd>.pdf) via a date-stamped fallback.
export async function downloadLvaLabels(companyId: string, body: LvaLabelRequest): Promise<void> {
  const blob = await apiBlob(`/companies/${companyId}/lva-items/labels.pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  downloadBlob(blob, `lva-labels-${ymd}.pdf`);
}

// Mutation wrapper so call sites get isPending/error UI state and a successful
// print refreshes the list (printed badges flip via qrPrintedAt). The raw
// `downloadLvaLabels` above is reused by callers that don't need invalidation.
export function useDownloadLvaLabels(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LvaLabelRequest) => downloadLvaLabels(companyId ?? '', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: lvaKey(companyId) }),
  });
}

// Per-item append-only movement history (feeds LvaMovementLog on the detail).
export function useLvaMovements(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['lva-movements', companyId, id] as const,
    queryFn: () => api<LvaMovementListRow[]>(`/companies/${companyId}/lva-items/${id}/movements`),
    enabled: !!companyId && !!id,
  });
}

// Company-wide movement feed (feeds the Movement tab table).
export function useCompanyLvaMovements(companyId: string | undefined) {
  return useQuery({
    queryKey: ['lva-movements', companyId] as const,
    queryFn: () => api<LvaMovementListRow[]>(`/companies/${companyId}/lva-movements`),
    enabled: !!companyId,
  });
}

// Per-company LvaCategory master (Add/Edit form category picker).
export function useLvaCategories(companyId: string | undefined) {
  return useQuery({
    queryKey: ['lva-categories', companyId] as const,
    queryFn: () => api<LvaCategory[]>(`/companies/${companyId}/lva-categories`),
    enabled: !!companyId,
  });
}

// Latest-effective capitalization/materiality policy for the active company.
// Advisory (display) only — the authoritative threshold check is server-side in
// createItem (resolveCapitalizationCap). The endpoint
// (GET companies/:companyId/lva-capitalization-policy, gated lva.read) returns
// the resolved row or null; when no policy is configured the form (Task 29
// LvaFormModal) falls back to LVA_COST_CAP, so the advisory cap degrades
// gracefully to the default.
export function useCompanyCapitalizationPolicy(companyId: string | undefined) {
  return useQuery({
    queryKey: ['lva-capitalization-policy', companyId] as const,
    queryFn: () =>
      api<CompanyCapitalizationPolicy>(`/companies/${companyId}/lva-capitalization-policy`),
    enabled: !!companyId,
    retry: false,
  });
}

// ============================================================
// Photo attachments. Uploads use multipart/form-data which `apiJson` cannot
// serialise, so we mirror `queries/asset-attachments.ts` and post through
// `apiMultipart` (auth + RFC-7807 error handling identical to `api()`, but the
// browser sets the multipart Content-Type/boundary itself).
// ============================================================

export interface LvaAttachment {
  id: string;
  lvaItemId: string;
  kind: 'IMAGE' | 'DOCUMENT';
  filename: string;
  mimeType: string;
  sizeBytes: number;
  storageKey: string;
  caption?: string | null;
  uploadedBy?: string | null;
  uploadedAt: string;
}

async function authHeaderForMultipart(): Promise<Record<string, string>> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return {};
  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: [ENTRA_API_SCOPE],
    });
    return { Authorization: `Bearer ${result.accessToken}` };
  } catch {
    return {};
  }
}

async function apiMultipart<T>(path: string, body: FormData): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(await authHeaderForMultipart()),
    // Important: do NOT set Content-Type — browser must add the boundary param.
  };
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', body, headers });
  if (res.status === 401) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  if (!res.ok) {
    let errBody: ApiError = { status: res.status };
    try {
      const parsed = (await res.json()) as Partial<ApiError>;
      errBody = { ...errBody, ...parsed };
    } catch {
      // non-JSON error body
    }
    throw new HttpError(res.status, errBody);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const attachKey = (companyId: string | undefined, id: string | undefined) =>
  ['lva-attachments', companyId, id] as const;

export function useLvaAttachments(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: attachKey(companyId, id),
    queryFn: () => api<LvaAttachment[]>(`/companies/${companyId}/lva-items/${id}/attachments`),
    enabled: !!companyId && !!id,
  });
}

export interface UploadLvaAttachmentInput {
  file: File;
  caption?: string;
  kind?: 'IMAGE' | 'DOCUMENT';
}

export function useUploadLvaAttachment(companyId: string | undefined, id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, caption, kind }: UploadLvaAttachmentInput) => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      if (caption) fd.append('caption', caption);
      if (kind) fd.append('kind', kind);
      return apiMultipart<LvaAttachment>(`/companies/${companyId}/lva-items/${id}/attachments`, fd);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: attachKey(companyId, id) }),
  });
}

export function useDeleteLvaAttachment(companyId: string | undefined, id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      api<LvaAttachment>(`/companies/${companyId}/lva-items/${id}/attachments/${attachmentId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: attachKey(companyId, id) }),
  });
}

// Presigned URL — short-lived (5 min on the server). staleTime of 4 min so
// React Query refreshes just before expiry instead of returning a stale URL.
// Mirrors queries/asset-attachments.ts useAttachmentDownloadUrl.
export interface LvaAttachmentDownloadUrl {
  url: string;
  expiresAt: string;
}

export function useLvaAttachmentDownloadUrl(
  companyId: string | undefined,
  id: string | undefined,
  attachmentId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['lva-attachment-url', companyId, id, attachmentId] as const,
    queryFn: () =>
      api<LvaAttachmentDownloadUrl>(
        `/companies/${companyId}/lva-items/${id}/attachments/${attachmentId}/url`,
      ),
    enabled: enabled && !!companyId && !!id && !!attachmentId,
    staleTime: 4 * 60 * 1000,
  });
}
